import React, { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { companyAPI } from '../services/api';
import BackButton from '../components/BackButton';
import { fmtDate } from '../utils/date';
import { PageError, loadError } from '../components/OfflineNotice';

// One product of a child business: who bought it (grouped by customer, then
// every sale) and who supplied it. Opened from the Sales or Purchases tab.
export default function BusinessProductDetail() {
  const { id, productId } = useParams();
  const [params] = useSearchParams();
  const purchasesFirst = params.get('from') === 'purchases';
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState('');

  useEffect(() => {
    companyAPI.getBizProductDetail(id, productId)
      .then(r => setData(r.data))
      .catch((err) => setError(loadError(err, 'Failed to load product')))
      .finally(() => setLoading(false));
  }, [id, productId]);

  const fmt = (n) =>
    new Intl.NumberFormat(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(Number(n) || 0);

  if (loading) return <div className="page"><BackButton /><p className="text-muted">Loading…</p></div>;
  if (error)   return <div className="page"><BackButton /><PageError error={error} /></div>;

  const { business, product, sales, purchases } = data;
  const cur = business.currency;

  // Group sales by customer; unnamed paid sales are walk-in customers.
  const byCustomer = new Map();
  for (const s of sales) {
    const name = (s.buyer_name || '').trim() || 'Walk-in customer';
    const key  = s.customer_id || `name:${name.toLowerCase()}`;
    const g = byCustomer.get(key) || { key, name, contact: s.buyer_contact, count: 0, qty: 0, total: 0, outstanding: 0, last: s.sold_at };
    g.count += 1;
    g.qty += Number(s.qty);
    g.total += Number(s.total_amount);
    g.outstanding += Number(s.outstanding);
    byCustomer.set(key, g);
  }
  const customers = [...byCustomer.values()].sort((a, b) => b.total - a.total);

  const totalSold        = sales.reduce((t, s) => t + Number(s.total_amount), 0);
  const totalQty         = sales.reduce((t, s) => t + Number(s.qty), 0);
  const totalOutstanding = sales.reduce((t, s) => t + Number(s.outstanding), 0);
  const totalBought      = purchases.reduce((t, p) => t + Number(p.total_cost), 0);

  const sectionHeader = (title, total, color) => (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: '0.5rem', marginTop: '1rem' }}>
      <span style={{ fontSize: '0.72rem', fontWeight: 700, color: 'var(--color-muted)', textTransform: 'uppercase', letterSpacing: '0.07em' }}>{title}</span>
      {total !== undefined && (
        <span style={{ fontWeight: 700, fontSize: '0.88rem', color: color || 'inherit' }}>{cur} {fmt(total)}</span>
      )}
    </div>
  );

  const salesSection = (
    <>
      {sectionHeader(`${customers.length} ${customers.length === 1 ? 'customer' : 'customers'}`, totalSold)}
      {customers.length === 0
        ? <p className="text-muted" style={{ fontSize: '0.88rem' }}>No one has bought this yet.</p>
        : customers.map(c => (
          <div key={c.key} className="card" style={{ marginBottom: '0.45rem' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <div>
                <div style={{ fontWeight: 600, fontSize: '0.88rem' }}>{c.name}</div>
                <div className="text-muted" style={{ fontSize: '0.75rem', marginTop: '0.1rem' }}>
                  {c.count} {c.count === 1 ? 'purchase' : 'purchases'} · qty {c.qty.toFixed(2)} · last {fmtDate(c.last)}
                  {c.contact ? ` · ${c.contact}` : ''}
                </div>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '0.2rem', flexShrink: 0, marginLeft: '0.75rem' }}>
                <span style={{ fontWeight: 700, fontSize: '0.88rem' }}>{cur} {fmt(c.total)}</span>
                {c.outstanding > 0 && (
                  <span style={{ fontSize: '0.72rem', color: 'var(--color-warning)' }}>{cur} {fmt(c.outstanding)} owed</span>
                )}
              </div>
            </div>
          </div>
        ))
      }

      {sales.length > 0 && (
        <>
          {sectionHeader(`${sales.length} ${sales.length === 1 ? 'sale' : 'sales'}`)}
          {sales.map(s => (
            <div key={s.id} className="card" style={{ marginBottom: '0.45rem' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div>
                  <div style={{ fontWeight: 600, fontSize: '0.88rem' }}>{(s.buyer_name || '').trim() || 'Walk-in customer'}</div>
                  <div className="text-muted" style={{ fontSize: '0.75rem', marginTop: '0.1rem' }}>
                    qty {Number(s.qty).toFixed(2)} @ {cur} {fmt(s.unit_price)} · {fmtDate(s.sold_at)}
                  </div>
                  {Number(s.outstanding) > 0 && (
                    <div style={{ fontSize: '0.75rem', color: 'var(--color-warning)', marginTop: '0.1rem' }}>
                      Outstanding: {cur} {fmt(s.outstanding)}
                    </div>
                  )}
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '0.2rem', flexShrink: 0, marginLeft: '0.75rem' }}>
                  <span style={{ fontWeight: 700, fontSize: '0.88rem' }}>{cur} {fmt(s.total_amount)}</span>
                  <span className={`badge badge-${s.payment_status}`} style={{ fontSize: '0.62rem' }}>{s.payment_status}</span>
                </div>
              </div>
            </div>
          ))}
        </>
      )}
    </>
  );

  const purchasesSection = (
    <>
      {sectionHeader(`${purchases.length} ${purchases.length === 1 ? 'purchase' : 'purchases'}`, totalBought, 'var(--color-danger)')}
      {purchases.length === 0
        ? <p className="text-muted" style={{ fontSize: '0.88rem' }}>No purchases recorded.</p>
        : purchases.map(p => (
          <div key={p.id} className="card" style={{ marginBottom: '0.45rem' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <div>
                <div style={{ fontWeight: 600, fontSize: '0.88rem' }}>{p.supplier || 'No supplier'}</div>
                <div className="text-muted" style={{ fontSize: '0.75rem', marginTop: '0.1rem' }}>
                  qty {Number(p.qty).toFixed(2)} @ {cur} {fmt(p.unit_cost)} · {fmtDate(p.purchased_at)}
                </div>
              </div>
              <span style={{ fontWeight: 700, fontSize: '0.88rem', color: 'var(--color-danger)', flexShrink: 0, marginLeft: '0.75rem' }}>
                − {cur} {fmt(p.total_cost)}
              </span>
            </div>
          </div>
        ))
      }
    </>
  );

  return (
    <div className="page">
      <BackButton />
      <h1 className="page-title">{product.name}</h1>
      <p className="text-muted" style={{ fontSize: '0.82rem', marginBottom: '1rem' }}>
        {business.name}{product.sku ? ` · SKU ${product.sku}` : ''}
      </p>

      <div className="kpi-grid" style={{ marginBottom: '0.5rem' }}>
        <div className="card kpi-card">
          <div className="kpi-label">Sold</div>
          <div className="kpi-value">{cur} {fmt(totalSold)}</div>
          <div className="text-muted" style={{ fontSize: '0.72rem', marginTop: '0.15rem' }}>qty {totalQty.toFixed(2)}</div>
        </div>
        <div className="card kpi-card">
          <div className="kpi-label">Outstanding</div>
          <div className="kpi-value" style={{ color: totalOutstanding > 0 ? 'var(--color-warning)' : 'inherit' }}>{cur} {fmt(totalOutstanding)}</div>
        </div>
      </div>

      {purchasesFirst ? <>{purchasesSection}{salesSection}</> : <>{salesSection}{purchasesSection}</>}
    </div>
  );
}
